import { useMemo, useState, type Ref } from "react";
import { parseQuickAdd } from "../domain/quickAdd";
import { TagPill } from "./TagPill";

interface QuickAddProps {
  /** The project new tasks go to when the text names none; empty means no project. */
  defaultProject?: string;
  onSubmit(text: string): Promise<boolean>;
  inputRef: Ref<HTMLInputElement>;
}

export function QuickAdd({ defaultProject = "", onSubmit, inputRef }: QuickAddProps) {
  const [text, setText] = useState("");
  const parsed = useMemo(() => parseQuickAdd(text), [text]);

  const submit = async () => {
    if (text.trim() === "") return;
    if (await onSubmit(text)) setText("");
  };

  return (
    <form
      className="quick-add"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <label className="visually-hidden" htmlFor="quick-add-input">
        Add a task
      </label>
      <input
        id="quick-add-input"
        ref={inputRef}
        className="quick-add-input"
        autoComplete="off"
        spellCheck={false}
        placeholder="Add task…  e.g. Pay rent due:friday +home recur:1m"
        aria-describedby="quick-add-help"
        value={text}
        onChange={(event) => setText(event.target.value)}
      />
      <div className="chips" aria-live="polite">
        {text.trim() !== "" && parsed.draft.project === undefined && (
          <span className="chip project">{defaultProject === "" ? "Inbox" : defaultProject}</span>
        )}
        {parsed.chips.map((chip, position) =>
          chip.kind === "tag" ? (
            <TagPill key={`${chip.kind}-${position}`} tag={chip.label.slice(1)} />
          ) : (
            <span key={`${chip.kind}-${position}`} className="chip">
              {chip.label}
            </span>
          ),
        )}
      </div>
      {parsed.problems.map((problem) => (
        <p key={problem} className="problem" role="alert">
          {problem}
        </p>
      ))}
      <p id="quick-add-help" className="hint">
        project: due: wait: priority: +tag recur:[every:|after:]2w until: count: notes:
      </p>
    </form>
  );
}
